<div class="mb-md flex flex-wrap items-center gap-md">
  <h2 class="reset-h text-lg font-semibold text-default">{{ 'Organization' | translate }}</h2>

  <div class="ml-auto flex items-center gap-sm empty:hidden">
    <sb-search-input class="w-60 shrink-0" [debounce]="300" [(value)]="searchQuery" />

    <button
      sb-icon-button
      color="secondary"
      sbMenuTriggerPlacement="bottom-end"
      [sbMenuTriggerFor]="statusMenu.panel"
      [sbTooltip]="'Filter' | translate"
    >
      <sb-icon name="filter-2-regular" />
      <span class="sr-only">{{ 'Filter' | translate }}</span>
    </button>
    <sb-menu #statusMenu>
      <button sbMenuItemCheckbox [(checked)]="showActive">{{ 'Active' | translate }}</button>
      <button sbMenuItemCheckbox [(checked)]="showInactive">{{ 'Inactive' | translate }}</button>
    </sb-menu>

    @if (menuItems().length > 1) {
      <button
        sb-button
        color="primary"
        sbMenuTriggerPlacement="bottom-end"
        [sbMenuTriggerFor]="createMenu.panel"
        [sbMenuTriggerDisabled]="hasReachedTeamQuota() && isFreePlan()"
        [disabled]="hasReachedTeamQuota() && isFreePlan()"
      >
        <sb-icon name="add-regular" />{{ 'Create' | translate }}
      </button>
      <sb-menu #createMenu>
        @for (menuItem of menuItems(); track menuItem.label) {
          <button sbMenuItem [leadingIcon]="menuItem.icon" (triggered)="menuItem.command()">
            {{ menuItem.label | translate }}
          </button>
        }
      </sb-menu>
    } @else if (menuItems().length > 0) {
      <button
        sb-button
        color="primary"
        [disabled]="hasReachedTeamQuota() && isFreePlan()"
        (click)="menuItems()[0].command()"
      >
        <sb-icon name="add-regular" />{{ menuItems()[0].label | translate }}
      </button>
    }
  </div>
</div>

<sb-card>
  @if (visibleLocations().length === 0) {
    <sb-empty-state
      class="p-2xl"
      icon="search-regular"
      [title]="'No results found' | translate"
      [description]="'Try adjusting your search or filters' | translate"
    />
  } @else {
    <sb-card-content-list>
      @for (locationRow of visibleLocations(); track locationRow.location.id) {
        @let location = locationRow.location;

        <sb-card-content-list-item
          variant="header"
          icon="building-1-cute-re"
          [ariaLabel]="location.name"
          [inactive]="locationRow.inactive"
        >
          {{ location.name }}
          <div slot="meta" class="flex items-center gap-sm">
            @if (locationRow.inactive) {
              <sb-badge color="grey">{{ 'Inactive' | translate }}</sb-badge>
            }
            <sb-badge color="grey">{{ 'Location' | translate }}</sb-badge>
          </div>
          <div slot="actions" class="flex items-center">
            <button
              sb-icon-button
              color="transparent"
              [attr.aria-label]="'Edit' | translate"
              [sbTooltip]="'Edit' | translate"
              [routerLink]="['', { outlets: { modal: ['location-group', location.id] } }]"
            >
              <sb-icon name="pencil-regular" />
            </button>
            @if (!location.deleted && !location.loading) {
              <span
                class="relative"
                [sbTooltip]="
                  locationsWithEmployees().has(location.id)
                    ? ('Relocate the employees in this location before you can delete it.' | translate)
                    : undefined
                "
              >
                <button
                  sb-icon-button
                  color="transparent"
                  [attr.aria-label]="'Delete' | translate"
                  [sbTooltip]="'Delete' | translate"
                  (click)="deactivateLocationGroup(location.id)"
                  [disabled]="locationsWithEmployees().has(location.id)"
                >
                  <sb-icon name="delete-2-regular" />
                </button>
              </span>
            }
            <ng-container *subscriptionQuota="quotaType.DEPARTMENTS">
              @if (location.deleted && !location.loading) {
                <button
                  sb-icon-button
                  color="transparent"
                  [attr.aria-label]="'Activate' | translate"
                  [sbTooltip]="'Activate' | translate"
                  (click)="activateLocationGroup(location.id)"
                >
                  <sb-icon name="check-regular" />
                </button>
              }
            </ng-container>
            @if (location.loading) {
              <sb-spinner
                class="flex size-8.5 items-center justify-center"
                [strokeColor]="location.deleted ? 'current' : 'grey'"
              />
            }
          </div>
        </sb-card-content-list-item>

        @for (departmentRow of locationRow.departments; track departmentRow.department.id) {
          @let department = departmentRow.department;

          <sb-card-content-list-item [level]="1" [ariaLabel]="department.name" [inactive]="departmentRow.inactive">
            {{ department.name }}
            <div slot="meta" class="flex items-center gap-sm">
              @if (departmentRow.inactive) {
                <sb-badge color="grey">{{ 'Inactive' | translate }}</sb-badge>
              }
              <sb-badge color="grey">{{ 'Department' | translate }}</sb-badge>
            </div>
            <div slot="actions" class="flex items-center">
              <button
                sb-icon-button
                color="transparent"
                [attr.aria-label]="'Edit' | translate"
                [sbTooltip]="'Edit' | translate"
                [routerLink]="['', { outlets: { modal: ['location', department.id] } }]"
              >
                <sb-icon name="pencil-regular" />
              </button>
              <ng-container *permission="'Manage account'; user: 'me'; department: department.id">
                @if (!department.deleted && !department.loading) {
                  <span class="relative">
                    <button
                      sb-icon-button
                      color="transparent"
                      [attr.aria-label]="'Delete' | translate"
                      [sbTooltip]="'Delete' | translate"
                      (click)="deactivateDepartment(department.id)"
                      [disabled]="departmentsCount() <= 1"
                    >
                      <sb-icon name="delete-2-regular" />
                    </button>
                  </span>
                }
              </ng-container>
              <ng-container *subscriptionQuota="quotaType.DEPARTMENTS">
                @if (department.deleted && !department.loading && !location.loading) {
                  <button
                    sb-icon-button
                    color="transparent"
                    [attr.aria-label]="'Activate' | translate"
                    [sbTooltip]="'Activate' | translate"
                    (click)="activateDepartment(department.id)"
                  >
                    <sb-icon name="check-regular" />
                  </button>
                }
              </ng-container>
              @if (department.loading || location.loading) {
                <sb-spinner
                  class="flex w-8.5 justify-center"
                  [strokeColor]="departmentRow.inactive ? 'current' : 'grey'"
                />
              }
            </div>
          </sb-card-content-list-item>

          @for (teamRow of departmentRow.teams; track teamRow.team.id) {
            @let team = teamRow.team;

            <sb-card-content-list-item [level]="2" [ariaLabel]="team.name" [inactive]="teamRow.inactive">
              @switch (team.type) {
                @case (TeamType.DEFAULT) {
                  <span slot="leading" class="relative size-4.5 rounded-md" [style.backgroundColor]="team.color"></span>
                }
                @case (TeamType.FLEXPOOL) {
                  <span
                    slot="leading"
                    class="relative flex size-4.5 items-center justify-center rounded-md"
                    [style.backgroundColor]="team.color"
                  >
                    <sb-icon
                      class="size-3"
                      [class.text-on-color-light]="team.color_is_dark"
                      [class.text-on-color-dark]="!team.color_is_dark"
                      name="refresh-3-regular"
                    />
                  </span>
                }
                @case (TeamType.HIDDEN) {
                  <span
                    slot="leading"
                    class="relative flex size-4.5 items-center justify-center rounded-md"
                    [style.backgroundColor]="team.color"
                  >
                    <sb-icon
                      class="size-3"
                      [class.text-on-color-light]="team.color_is_dark"
                      [class.text-on-color-dark]="!team.color_is_dark"
                      name="eye-close-regular"
                    />
                  </span>
                }
              }
              {{ team.name }}
              <div slot="meta" class="flex items-center gap-sm">
                @if (teamRow.inactive) {
                  <sb-badge color="grey">{{ 'Inactive' | translate }}</sb-badge>
                }
                <sb-badge color="grey">{{ 'Team' | translate }}</sb-badge>
              </div>
              <div slot="actions" class="flex items-center">
                <button
                  *permission="'Edit teams'; user: 'me'; department: team.department_id"
                  sb-icon-button
                  color="transparent"
                  [attr.aria-label]="'Edit' | translate"
                  [sbTooltip]="'Edit' | translate"
                  [routerLink]="['', { outlets: { modal: ['team', 'edit', team.id] } }]"
                >
                  <sb-icon name="pencil-regular" />
                </button>
                <ng-container *permission="'Delete teams'; user: 'me'; department: team.department_id">
                  @if (!team.deleted && !team.loading && !department.loading) {
                    <span
                      class="relative"
                      [sbTooltip]="
                        departmentRow.activeTeamCount > 1
                          ? undefined
                          : ('Every department must have, at minimum, one team.' | translate)
                      "
                    >
                      <button
                        sb-icon-button
                        color="transparent"
                        [attr.aria-label]="'Delete team' | translate"
                        [sbTooltip]="'Delete team' | translate"
                        [appDeleteTeam]="{ teamId: team.id, departmentId: department.id }"
                        [disabled]="departmentRow.activeTeamCount <= 1"
                      >
                        <sb-icon name="delete-2-regular" />
                      </button>
                    </span>
                  }
                </ng-container>
                @if (team.loading || department.loading) {
                  <sb-spinner class="flex w-8.5 justify-center" [strokeColor]="teamRow.inactive ? 'current' : 'grey'" />
                }
              </div>
            </sb-card-content-list-item>
          }
        }
      }
    </sb-card-content-list>
  }
</sb-card>
